import { Fragment, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import 'react-datepicker/dist/react-datepicker.css';

import { editarBreadcrumb } from '../../../actions/breadcrumb';
import { setAlert } from '../../../actions/alert';
import { obtenerDetalleProyecto, limpiarDetalleProyecto } from '../../../actions/proyectos';
import {
    obtenerOrden,
    editarOrden,
    limpiarDetalleOrden,
    buscarInventarioEntregado,
    limpiarInventarioEntregado
} from '../../../actions/ordenesDevolucion';

const formatNumber = (value) => {
    const v = Number(value || 0);
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(v);
};

const formatYYYYMMDD = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return null;
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const parseFecha = (value) => {
    if (!value) return new Date();
    const d = new Date(value);
    return isNaN(d.getTime()) ? new Date() : d;
};

/* El mismo producto+unidad puede venir de distintas órdenes de salida del proyecto — la clave
   debe incluir orden_salida para no mezclar sus disponibles ni perder a cuál pertenece cada fila. */
const buildKey = (idOrdenSalida, idProducto, idUnidad) => `${idOrdenSalida || 'na'}_${idProducto}_${idUnidad || 'na'}`;

const EditarOrdenDevolucion = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const { detalle: orden, loadingDetalle } = useSelector(state => state.ordenesDevolucion);
    const { inventarioEntregado, paquetesEntregado, loadingInventarioEntregado } = useSelector(state => state.ordenesDevolucion);
    const { detalle: proyecto } = useSelector(state => state.proyectos);

    const [fecha, setFecha] = useState(new Date());
    const [observaciones, setObservaciones] = useState('');

    const [productoSel, setProductoSel] = useState(null);
    const [cantidadProducto, setCantidadProducto] = useState('');

    const [productos, setProductos] = useState([]);

    const [paqueteSel, setPaqueteSel] = useState(null);
    const [cantidadPaquete, setCantidadPaquete] = useState('');

    const [paquetes, setPaquetes] = useState([]);

    /* Snapshot fijo de lo que esta orden ya tenía guardado en BD por producto+unidad (o por
       orden_salida_paquete, para paquetes completos) al cargar. No se actualiza con las ediciones
       del usuario: el backend, al calcular "disponible", excluye exactamente estas cantidades (las
       de esta misma orden), así que hay que volver a sumárselas para reconstruir el disponible real. */
    const [cantidadOriginalPorKey, setCantidadOriginalPorKey] = useState({});
    const [cantidadOriginalPorKeyPaquete, setCantidadOriginalPorKeyPaquete] = useState({});

    const [guardando, setGuardando] = useState(false);
    const [cargaInicial, setCargaInicial] = useState(false);

    useEffect(() => {
        if (!id) return;

        dispatch(obtenerOrden(id));

        return () => {
            dispatch(limpiarDetalleOrden());
            dispatch(limpiarInventarioEntregado());
        };
    }, [dispatch, id]);

    useEffect(() => {
        if (!orden?.proyecto?._id) return;

        dispatch(buscarInventarioEntregado(orden.proyecto._id));
        dispatch(obtenerDetalleProyecto(orden.proyecto._id));

        return () => {
            dispatch(limpiarDetalleProyecto());
        };
    }, [dispatch, orden?.proyecto?._id]);

    useEffect(() => {
        if (!orden || cargaInicial) return;

        if (Number(orden.status) !== 1) {
            dispatch(setAlert('Solo se pueden editar órdenes con status Creada', 'danger'));
            navigate(`/proyectos/detalle/${orden?.proyecto?._id || ''}`);
            return;
        }

        setFecha(parseFecha(orden.fecha));
        setObservaciones(orden.observaciones || '');

        const productosStandalone = (orden.productos || []).filter(p => !p.orden_salida_paquete);
        const productosPaquetes = (orden.productos || []).filter(p => !p.producto && !!p.orden_salida_paquete);

        setProductos(productosStandalone.map((p) => ({
            key: buildKey(p.orden_salida, p.producto?._id, p.unidad?._id),
            ordenSalida: p.orden_salida || null,
            producto: p.producto?._id || null,
            productoNombre: p.producto?.nombre || 'Sin definir',
            unidad: p.unidad?._id || null,
            unidadNombre: p.unidad?.nombre || 'Sin unidad',
            cantidad: Number(p.cantidad || 0)
        })));

        const original = {};
        productosStandalone.forEach((p) => {
            const key = buildKey(p.orden_salida, p.producto?._id, p.unidad?._id);
            original[key] = (original[key] || 0) + Number(p.cantidad || 0);
        });
        setCantidadOriginalPorKey(original);

        setPaquetes(productosPaquetes.map((p) => ({
            key: String(p.orden_salida_paquete),
            orden_salida_paquete: String(p.orden_salida_paquete),
            paqueteNombre: p.paquete?.nombre || 'Sin definir',
            paqueteCodigo: p.paquete?.codigo || null,
            cantidad: Number(p.cantidad || 0)
        })));

        const originalPaquetes = {};
        productosPaquetes.forEach((p) => {
            const key = String(p.orden_salida_paquete);
            originalPaquetes[key] = (originalPaquetes[key] || 0) + Number(p.cantidad || 0);
        });
        setCantidadOriginalPorKeyPaquete(originalPaquetes);

        setCargaInicial(true);
    }, [orden, cargaInicial, dispatch, navigate]);

    useEffect(() => {
        dispatch(editarBreadcrumb([
            { activo: false, nombre: 'Proyectos', path: '/proyectos' },
            { activo: false, nombre: orden?.proyecto?.nombre || 'Proyecto', path: `/proyectos/detalle/${orden?.proyecto?._id || ''}` },
            { activo: true, nombre: `Editar devolución ${orden?.folio || ''}`.trim(), path: `/proyectos/devoluciones/editar/${id}` }
        ]));
    }, [dispatch, id, orden]);

    /* El disponible real de cada producto es el disponible reportado por el backend (que ya excluye
       lo devuelto por TODAS las órdenes activas, incluida esta misma) más lo que esta orden ya tenía
       guardado en BD originalmente (cantidadOriginalPorKey, fijo). Se calcula solo a partir de ese
       snapshot fijo, nunca de las cantidades que el usuario va editando en pantalla, porque el
       backend ya las excluyó una sola vez y no debe volver a sumarse ni descontarse en cada cambio. */
    const disponiblePorKey = useMemo(() => {
        const mapa = {};

        (inventarioEntregado || []).forEach((item) => {
            const key = buildKey(item.orden_salida, item.producto?._id, item.unidad?._id);
            mapa[key] = Number(item.cantidad_disponible || 0);
        });

        Object.entries(cantidadOriginalPorKey).forEach(([key, cantidad]) => {
            mapa[key] = (mapa[key] || 0) + Number(cantidad || 0);
        });

        return mapa;
    }, [inventarioEntregado, cantidadOriginalPorKey]);

    const foliosPorOrdenSalida = useMemo(() => {
        const mapa = {};
        (proyecto?.ordenes || []).forEach((o) => { mapa[String(o._id)] = o.folio; });
        return mapa;
    }, [proyecto]);

    const opcionesProductos = useMemo(() => {
        const opciones = (inventarioEntregado || []).map(item => {
            const key = buildKey(item.orden_salida, item.producto?._id, item.unidad?._id);
            const disponible = disponiblePorKey[key] ?? Number(item.cantidad_disponible || 0);
            const folio = foliosPorOrdenSalida[String(item.orden_salida)];

            return {
                value: key,
                label: `${item.producto?.nombre || 'Sin definir'} (${item.unidad?.nombre || 'Sin unidad'}) — Orden ${folio || 'Sin definir'} — Disponible: ${formatNumber(disponible)}`,
                raw: { orden_salida: item.orden_salida, producto: item.producto, unidad: item.unidad, cantidad_disponible: disponible }
            };
        });

        const keysExistentes = new Set(opciones.map(o => o.value));

        /* Productos que esta orden ya devuelve por completo (el backend los excluye de
           inventario_entregado porque su disponible, excluyendo esta orden, quedó en 0) siguen
           necesitando aparecer como opción para poder ajustarles la cantidad desde el buscador. */
        productos.forEach((p) => {
            if (keysExistentes.has(p.key)) return;

            const disponible = disponiblePorKey[p.key] ?? 0;
            if (disponible <= 0) return;

            keysExistentes.add(p.key);
            const folio = foliosPorOrdenSalida[String(p.ordenSalida)];
            opciones.push({
                value: p.key,
                label: `${p.productoNombre} (${p.unidadNombre}) — Orden ${folio || 'Sin definir'} — Disponible: ${formatNumber(disponible)}`,
                raw: {
                    orden_salida: p.ordenSalida,
                    producto: { _id: p.producto, nombre: p.productoNombre },
                    unidad: { _id: p.unidad, nombre: p.unidadNombre },
                    cantidad_disponible: disponible
                }
            });
        });

        return opciones;
    }, [inventarioEntregado, disponiblePorKey, productos, foliosPorOrdenSalida]);

    const disponibleSeleccionado = disponiblePorKey[productoSel?.value] ?? 0;

    /* Mismo razonamiento que disponiblePorKey, pero para paquetes completos: el backend ya
       excluye lo que esta misma orden tenía guardado, así que se vuelve a sumar aquí. */
    const disponiblePorKeyPaquete = useMemo(() => {
        const mapa = {};

        (paquetesEntregado || []).forEach((item) => {
            const key = String(item.orden_salida_paquete);
            mapa[key] = Number(item.cantidad_disponible || 0);
        });

        Object.entries(cantidadOriginalPorKeyPaquete).forEach(([key, cantidad]) => {
            mapa[key] = (mapa[key] || 0) + Number(cantidad || 0);
        });

        return mapa;
    }, [paquetesEntregado, cantidadOriginalPorKeyPaquete]);

    const opcionesPaquetes = useMemo(() => {
        const opciones = (paquetesEntregado || []).map(item => {
            const key = String(item.orden_salida_paquete);
            const disponible = disponiblePorKeyPaquete[key] ?? Number(item.cantidad_disponible || 0);

            return {
                value: key,
                label: `${item.paquete?.nombre || 'Sin definir'} — Disponible: ${formatNumber(disponible)}`,
                raw: {
                    orden_salida_paquete: key,
                    paquete: item.paquete,
                    cantidad_disponible: disponible
                }
            };
        });

        const keysExistentes = new Set(opciones.map(o => o.value));

        paquetes.forEach((p) => {
            if (keysExistentes.has(p.key)) return;

            const disponible = disponiblePorKeyPaquete[p.key] ?? 0;
            if (disponible <= 0) return;

            keysExistentes.add(p.key);
            opciones.push({
                value: p.key,
                label: `${p.paqueteNombre} — Disponible: ${formatNumber(disponible)}`,
                raw: {
                    orden_salida_paquete: p.orden_salida_paquete,
                    paquete: { nombre: p.paqueteNombre, codigo: p.paqueteCodigo },
                    cantidad_disponible: disponible
                }
            });
        });

        return opciones;
    }, [paquetesEntregado, disponiblePorKeyPaquete, paquetes]);

    const disponiblePaqueteSeleccionado = disponiblePorKeyPaquete[paqueteSel?.value] ?? 0;

    const limpiarFormularioProducto = () => {
        setProductoSel(null);
        setCantidadProducto('');
    };

    const puedeAgregarProducto = useMemo(() => {
        return Boolean(productoSel?.value && Number(cantidadProducto) > 0 && Number(cantidadProducto) <= disponibleSeleccionado);
    }, [productoSel, cantidadProducto, disponibleSeleccionado]);

    const onAgregarProducto = () => {
        if (!puedeAgregarProducto) return;

        const item = productoSel.raw;
        const idProducto = item.producto?._id;
        const idUnidad = item.unidad?._id;
        const idOrdenSalida = item.orden_salida;
        const key = buildKey(idOrdenSalida, idProducto, idUnidad);
        const disponibleTotal = disponiblePorKey[key] ?? 0;

        setProductos((prev) => {
            const idx = prev.findIndex(p => p.key === key);

            if (idx === -1) {
                return [
                    ...prev,
                    {
                        key,
                        ordenSalida: idOrdenSalida,
                        producto: idProducto,
                        productoNombre: item.producto?.nombre || 'Sin definir',
                        unidad: idUnidad,
                        unidadNombre: item.unidad?.nombre || 'Sin unidad',
                        cantidad: Number(cantidadProducto)
                    }
                ];
            }

            const copy = [...prev];
            const nuevaCantidad = Math.min(
                Number(copy[idx].cantidad || 0) + Number(cantidadProducto),
                disponibleTotal
            );
            copy[idx] = { ...copy[idx], cantidad: nuevaCantidad };
            return copy;
        });

        limpiarFormularioProducto();
    };

    const onEliminarProducto = (key) => {
        setProductos((prev) => prev.filter(p => p.key !== key));
    };

    const onChangeCantidadFila = (key, value) => {
        const max = disponiblePorKey[key] ?? 0;

        setProductos((prev) => prev.map(p => {
            if (p.key !== key) return p;
            if (value === '') return { ...p, cantidad: '' };
            const clamped = Math.max(0, Math.min(Number(value), max));
            return { ...p, cantidad: clamped };
        }));
    };

    const limpiarFormularioPaquete = () => {
        setPaqueteSel(null);
        setCantidadPaquete('');
    };

    const puedeAgregarPaquete = useMemo(() => {
        return Boolean(paqueteSel?.value && Number(cantidadPaquete) > 0 && Number(cantidadPaquete) <= disponiblePaqueteSeleccionado);
    }, [paqueteSel, cantidadPaquete, disponiblePaqueteSeleccionado]);

    const onAgregarPaquete = () => {
        if (!puedeAgregarPaquete) return;

        const item = paqueteSel.raw;
        const key = String(item.orden_salida_paquete);
        const disponibleTotal = disponiblePorKeyPaquete[key] ?? 0;

        setPaquetes((prev) => {
            const idx = prev.findIndex(p => p.key === key);

            if (idx === -1) {
                return [
                    ...prev,
                    {
                        key,
                        orden_salida_paquete: key,
                        paqueteNombre: item.paquete?.nombre || 'Sin definir',
                        paqueteCodigo: item.paquete?.codigo || null,
                        cantidad: Number(cantidadPaquete)
                    }
                ];
            }

            const copy = [...prev];
            const nuevaCantidad = Math.min(
                Number(copy[idx].cantidad || 0) + Number(cantidadPaquete),
                disponibleTotal
            );
            copy[idx] = { ...copy[idx], cantidad: nuevaCantidad };
            return copy;
        });

        limpiarFormularioPaquete();
    };

    const onEliminarPaquete = (key) => {
        setPaquetes((prev) => prev.filter(p => p.key !== key));
    };

    const onChangeCantidadFilaPaquete = (key, value) => {
        const max = disponiblePorKeyPaquete[key] ?? 0;

        setPaquetes((prev) => prev.map(p => {
            if (p.key !== key) return p;
            if (value === '') return { ...p, cantidad: '' };
            const clamped = Math.max(0, Math.min(Number(value), max));
            return { ...p, cantidad: clamped };
        }));
    };

    const puedeGuardar = useMemo(() => {
        return Boolean(
            id &&
            (productos.length > 0 || paquetes.length > 0) &&
            productos.every(p => Number(p.cantidad) > 0) &&
            paquetes.every(p => Number(p.cantidad) > 0) &&
            !guardando
        );
    }, [id, productos, paquetes, guardando]);

    const onGuardar = async () => {
        if (!puedeGuardar) return;

        if (productos.length === 0 && paquetes.length === 0) {
            dispatch(setAlert('Agrega al menos un producto o paquete', 'danger'));
            return;
        }

        setGuardando(true);
        const ordenActualizada = await dispatch(editarOrden({
            id,
            fecha: formatYYYYMMDD(fecha),
            observaciones: observaciones.trim() || null,
            productos: productos.map(p => ({
                producto: p.producto,
                unidad: p.unidad,
                cantidad: p.cantidad,
                orden_salida: p.ordenSalida
            })),
            paquetes: paquetes.map(p => ({
                orden_salida_paquete: p.orden_salida_paquete,
                cantidad: p.cantidad
            }))
        }));
        setGuardando(false);

        if (!ordenActualizada) return;

        navigate(`/proyectos/devoluciones/detalle/${id}`);
    };

    if (loadingDetalle || !orden) {
        return (
            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <h3 className="my-5">Cargando orden de devolución...</h3>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <h3 className="my-5 mb-1">Editar orden de devolución {orden?.folio || ''}</h3>
                    <h6 className="text-muted mb-4">{orden?.proyecto?.nombre || 'Proyecto'}</h6>

                    <div className="row g-3 mb-4">
                        <div className="col-md-3">
                            <div className="text-muted small mb-1">Fecha</div>
                            <DatePicker
                                selected={fecha}
                                onChange={(d) => setFecha(d)}
                                className="form-control"
                                dateFormat="dd/MM/yyyy"
                            />
                        </div>
                        <div className="col-md-9">
                            <div className="text-muted small mb-1">Observaciones</div>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Observaciones (opcional)"
                                value={observaciones}
                                onChange={(e) => setObservaciones(e.target.value)}
                            />
                        </div>
                    </div>

                    <h5 className="mb-3">Productos</h5>

                    <div className="text-muted mb-2" style={{ fontSize: 13 }}>
                        Solo se listan productos entregados mediante órdenes de salida completadas de este proyecto, con cantidad disponible para devolver.
                    </div>

                    <div className="row g-3 mb-3">
                        <div className="col-md-8">
                            <div className="text-muted small mb-1">Producto entregado</div>
                            <Select
                                options={opcionesProductos}
                                value={productoSel}
                                onChange={(opt) => { setProductoSel(opt || null); setCantidadProducto(''); }}
                                isClearable
                                isLoading={loadingInventarioEntregado}
                                placeholder="Producto entregado..."
                                noOptionsMessage={() => 'Sin productos disponibles para devolver'}
                            />
                        </div>
                        <div className="col-md-2">
                            <div className="text-muted small mb-1">Cantidad</div>
                            <input
                                type="number"
                                className="form-control"
                                placeholder="Cantidad"
                                value={cantidadProducto}
                                min={0}
                                max={disponibleSeleccionado || undefined}
                                onChange={(e) => setCantidadProducto(e.target.value)}
                                disabled={!productoSel}
                            />
                        </div>
                        <div className="col-md-2 d-flex align-items-center justify-content-end">
                            <button
                                type="button"
                                className="btn btn-outline-primary w-100"
                                onClick={onAgregarProducto}
                                disabled={!puedeAgregarProducto}
                            >
                                <i className="feather icon-plus" /> Agregar
                            </button>
                        </div>
                    </div>

                    <div className="table-responsive mb-4">
                        <table className="table table-striped table-hover">
                            <thead>
                                <tr>
                                    <th>Producto</th>
                                    <th>Unidad</th>
                                    <th>Orden de salida</th>
                                    <th className="text-end">Cantidad a devolver</th>
                                    <th className="text-end">Disponible</th>
                                    <th className="text-end">Opciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {productos.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="text-center text-muted">
                                            Sin productos agregados
                                        </td>
                                    </tr>
                                )}

                                {productos.map((p) => {
                                    const disponibleFila = disponiblePorKey[p.key] ?? 0;

                                    return (
                                        <tr key={p.key}>
                                            <td>{p.productoNombre}</td>
                                            <td>{p.unidadNombre}</td>
                                            <td>{foliosPorOrdenSalida[String(p.ordenSalida)] || 'Sin definir'}</td>
                                            <td className="text-end" style={{ minWidth: 110 }}>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm text-end"
                                                    value={p.cantidad}
                                                    min={0}
                                                    max={disponibleFila}
                                                    onChange={(e) => onChangeCantidadFila(p.key, e.target.value)}
                                                />
                                            </td>
                                            <td className="text-end">{formatNumber(disponibleFila)}</td>
                                            <td className="text-end">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-link px-2"
                                                    title="Eliminar"
                                                    onClick={() => onEliminarProducto(p.key)}
                                                >
                                                    <i className="fas fa-trash text-danger" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <h5 className="mb-3">Paquetes</h5>

                    <div className="text-muted mb-2" style={{ fontSize: 13 }}>
                        Paquetes entregados completos en una orden de salida completada, con cantidad disponible para devolver. Solo se pueden
                        devolver paquetes completos, no productos sueltos de su interior.
                    </div>

                    <div className="row g-3 mb-3">
                        <div className="col-md-8">
                            <div className="text-muted small mb-1">Paquete entregado</div>
                            <Select
                                options={opcionesPaquetes}
                                value={paqueteSel}
                                onChange={(opt) => { setPaqueteSel(opt || null); setCantidadPaquete(''); }}
                                isClearable
                                isLoading={loadingInventarioEntregado}
                                placeholder="Paquete entregado..."
                                noOptionsMessage={() => 'Sin paquetes disponibles para devolver'}
                            />
                        </div>
                        <div className="col-md-2">
                            <div className="text-muted small mb-1">Cantidad</div>
                            <input
                                type="number"
                                className="form-control"
                                placeholder="Cantidad"
                                value={cantidadPaquete}
                                min={0}
                                max={disponiblePaqueteSeleccionado || undefined}
                                onChange={(e) => setCantidadPaquete(e.target.value)}
                                disabled={!paqueteSel}
                            />
                        </div>
                        <div className="col-md-2 d-flex align-items-center justify-content-end">
                            <button
                                type="button"
                                className="btn btn-outline-primary w-100"
                                onClick={onAgregarPaquete}
                                disabled={!puedeAgregarPaquete}
                            >
                                <i className="feather icon-plus" /> Agregar
                            </button>
                        </div>
                    </div>

                    <div className="table-responsive mb-4">
                        <table className="table table-striped table-hover">
                            <thead>
                                <tr>
                                    <th>Paquete</th>
                                    <th className="text-end">Cantidad a devolver</th>
                                    <th className="text-end">Disponible</th>
                                    <th className="text-end">Opciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paquetes.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="text-center text-muted">
                                            Sin paquetes agregados
                                        </td>
                                    </tr>
                                )}

                                {paquetes.map((p) => {
                                    const disponibleFila = disponiblePorKeyPaquete[p.key] ?? 0;

                                    return (
                                        <tr key={p.key}>
                                            <td>
                                                {p.paqueteNombre}
                                                {p.paqueteCodigo ? (
                                                    <div className="text-muted" style={{ fontSize: 12 }}>{p.paqueteCodigo}</div>
                                                ) : null}
                                            </td>
                                            <td className="text-end" style={{ minWidth: 110 }}>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm text-end"
                                                    value={p.cantidad}
                                                    min={0}
                                                    max={disponibleFila}
                                                    onChange={(e) => onChangeCantidadFilaPaquete(p.key, e.target.value)}
                                                />
                                            </td>
                                            <td className="text-end">{formatNumber(disponibleFila)}</td>
                                            <td className="text-end">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-link px-2"
                                                    title="Eliminar"
                                                    onClick={() => onEliminarPaquete(p.key)}
                                                >
                                                    <i className="fas fa-trash text-danger" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-end">
                        <div className="d-flex gap-2">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => navigate(`/proyectos/devoluciones/detalle/${id}`)}
                                disabled={guardando}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={onGuardar}
                                disabled={!puedeGuardar}
                            >
                                Guardar cambios
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default EditarOrdenDevolucion;
